import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ToolsBackdrop from "@/components/ToolsBackdrop";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, pw);
      toast.success("Connexion réussie");
      nav(u.role === "pro" ? "/pro/home" : "/p/home", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="app-shell grain-bg tools-bg flex flex-col">
      <ToolsBackdrop />
      <header className="px-5 py-4 flex items-center gap-3">
        <button onClick={() => nav("/onboarding")} className="tap-scale text-plate-tight" data-testid="login-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <h2 className="font-heading font-bold text-lg text-plate-tight">Connexion</h2>
      </header>
      <form onSubmit={submit} className="px-5 py-4 space-y-5">
        <div className="auth-panel space-y-5">
          <div className="text-center">
            <img src="/brand/icon-192.png" alt="Besoin d'un Pro" className="h-16 w-16 mx-auto rounded-2xl mb-3 shadow-md" />
            <h1 className="text-2xl font-heading font-bold">Bon retour</h1>
            <p className="text-sm text-slate2 mt-1">Connectez-vous à votre compte Besoin d&apos;un Pro&nbsp;?</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate2">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input data-testid="login-email-input" type="email" className="pl-10 rounded-xl h-12" placeholder="vous@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate2">Mot de passe</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input data-testid="login-password-input" type="password" className="pl-10 rounded-xl h-12" placeholder="••••••••" value={pw} onChange={(e) => setPw(e.target.value)} />
            </div>
          </div>

          <Button type="submit" disabled={busy} data-testid="login-submit-btn"
            className="w-full rounded-full bg-brand hover:bg-brand-hover text-white py-6 text-base font-semibold">
            {busy ? "Connexion…" : "Se connecter"}
          </Button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[11px] uppercase tracking-widest text-slate2">ou</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            type="button"
            data-testid="login-google-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + "/auth-callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full bg-white border border-border rounded-full h-12 flex items-center justify-center gap-3 tap-scale"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.4C29.7 34.5 27 35.5 24 35.5c-5.2 0-9.7-3.3-11.3-8l-6.6 5.1C9.5 39.5 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.6 5.4C41.9 35.7 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            <span className="text-sm font-semibold text-ink">Continuer avec Google</span>
          </button>

          <div className="text-center text-xs text-slate2">
            Pas encore inscrit ?{" "}
            <button type="button" onClick={() => nav("/onboarding")} className="text-brand font-semibold" data-testid="goto-register-btn">Créer un compte</button>
          </div>
        </div>
      </form>
    </div>
  );
}
